class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);



// sof-cart-upsells — single-card carousel, breed-matched, size-matched.
// Pulls breed-only recommendations via section_id=related-products; on Add,
// reads current cart's first item variant title and picks the matching
// variant on the upsell product (so 6ft carts beget 6ft upsells).
const SOF_BREED_HANDLE = {"Australian Shepherd": "australian-shepherds", "Beagle": "beagles", "Bernese Mountain Dog": "bernese-mountain-dogs", "Black Labrador Retriever": "black-labradors", "Border Collie": "border-collies", "Boston Terrier": "boston-terriers", "Boxer": "boxers", "Cavalier King Charles Spaniel": "cavalier-king-charles-spaniels", "Cavapoo": "cavapoos", "Cocker Spaniel": "cocker-spaniels", "Collie": "collies", "Corgi": "corgis", "Dachshund": "dachshunds", "Long-Haired Dachshund": "dachshunds", "Dalmatian": "dalmatians", "Doberman": "dobermans", "English Bulldog": "english-bulldogs", "French Bulldog": "french-bulldogs", "German Shepherd": "german-shepherds", "Golden Retriever": "golden-retrievers", "English Cream Golden Retriever": "golden-retrievers", "Goldendoodle": "goldendoodles", "Great Dane": "great-danes", "Maltipoo": "maltipoos", "Mutt": "mutts", "Poodle": "poodles", "Pug": "pugs", "Rottweiler": "rottweilers", "Sheltie": "shelties", "Shiba Inu": "shiba-inus", "Shih Tzu": "shih-tzus", "Siberian Husky": "siberian-huskies", "Yellow Labrador Retriever": "yellow-labradors", "Yorkshire Terrier": "yorkshire-terriers", "Basset Hound": "basset-hounds", "Bullmastiff": "bullmastiffs", "Chihuahua": "chihuahuas", "Chocolate Labrador Retriever": "labrador-retrievers", "German Shorthaired Pointer": "german-shorthaired-pointers", "Great Pyrenees": "great-pyrenees", "Jack Russell Terrier": "jack-russell-terriers", "Miniature Schnauzer": "miniature-schnauzers", "Norwich Terrier": "norwich-terriers", "Samoyed": "samoyeds", "Scottish Terrier": "scottish-terriers", "Vizsla": "vizslas", "Westie": "westies", "Newfoundland": "newfoundlands"};
class SofCartUpsells extends HTMLElement {
  connectedCallback() {
    const grid = this.querySelector('[data-upsell-grid]');
    if (!grid) return;
    this.loadUpsells(grid);
  }

  // Same-breed upsells in Shopify's native BEST-SELLING order, with each
  // product's exact breed metafield — via the Section Rendering API on the
  // API-only section `sof-upsell-feed` (products.json ignores sort_by).
  async fetchBreedProducts(handle) {
    try {
      const r = await fetch(`/collections/${handle}?sort_by=best-selling&section_id=sof-upsell-feed`, { headers: { Accept: 'text/html' } });
      if (!r.ok) return [];
      const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
      const node = doc.querySelector('[data-sof-upsell-feed]');
      if (!node) return [];
      return (JSON.parse(node.textContent) || []).filter(Boolean).map(p => ({
        href: `/products/${p.handle}`,
        handle: p.handle,
        imgSrc: p.image || '',
        title: p.title,
        breed: p.breed || '',
        ptype: p.ptype || '',
        price: p.price || '',
        rating: Number(p.rating) || 0,
        ratingCount: Number(p.rating_count) || 0,
      })).filter(p => p.handle && p.title);
    } catch { return []; }
  }

  // Fetch recommended products via Shopify's JSON recommendations API.
  // JSON (not section-HTML) means there is no markup to parse or break.
  async fetchRecos(pid, intent) {
    const url = `/recommendations/products.json?product_id=${pid}&limit=8`
      + (intent ? `&intent=${intent}` : '');
    try {
      const r = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!r.ok) return [];
      const data = await r.json();
      return (data.products || []).map(p => ({
        href: p.url,
        handle: p.handle,
        imgSrc: p.featured_image || (p.images && p.images[0]) || '',
        title: p.title,
      })).filter(p => p.handle && p.title);
    } catch { return []; }
  }

  async loadUpsells(grid) {
    // SAME-BREED upsells (Brian, 2026-06-01): pull products from the breed
    // collection(s) of the items already in the cart, excluding those items.
    // Fall back to Shopify related recs only when no breed can be resolved.
    const breeds = (this.dataset.breeds || '').split('~').map(x => x.trim()).filter(Boolean);
    const cartHandles = new Set((this.dataset.cartHandles || '').split('~').map(x => x.trim()).filter(Boolean));
    const handles = [...new Set(breeds.map(b => SOF_BREED_HANDLE[b]).filter(Boolean))];
    // Brian 2026-09-07: EXACT breed match (English Cream Golden ≠ Golden), the
    // TWO best sellers of that breed not already in the cart, stacked (no swipe).
    const MAX = 2;
    let products = [];
    if (handles.length) {
      const lists = await Promise.all(handles.map(h => this.fetchBreedProducts(h)));
      const wanted = new Set(breeds);
      const seen = new Set();
      for (const list of lists) for (const p of list) {
        if (!wanted.has(p.breed) || cartHandles.has(p.handle) || seen.has(p.handle)) continue;
        seen.add(p.handle); products.push(p);
      }
      products = products.slice(0, MAX);
    }
    if (!products.length) {
      const pid = this.dataset.productId;
      if (pid) products = (await this.fetchRecos(pid, 'related')).slice(0, MAX);
    }
    if (!products.length) { this.style.display = 'none'; return; }

    const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, m => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[m]));
    const wrapper = document.createElement('div');
    wrapper.className = 'sof-upsells__stack';
    wrapper.innerHTML = products.map(p => `
      <article class="sof-upsells__card">
        <a class="sof-upsells__thumb-link" href="${esc(p.href)}" aria-label="${esc(p.title)}">
          ${p.imgSrc ? `<img class="sof-upsells__thumb" src="${esc(p.imgSrc)}" alt="" loading="lazy">` : ''}
        </a>
        <div class="sof-upsells__body">
          <a class="sof-upsells__title-link" href="${esc(p.href)}">${esc(p.title)}</a>
          <span class="sof-upsells__meta">
            ${p.ratingCount > 0 ? `<span class="sof-upsells__stars" role="img" aria-label="${p.rating.toFixed(1)} out of 5 stars, ${p.ratingCount} reviews"><span class="sof-upsells__stars-fill" style="width:${Math.max(0, Math.min(100, p.rating / 5 * 100))}%"></span></span><span class="sof-upsells__count">(${p.ratingCount})</span>` : ''}
            ${p.price ? `<span class="sof-upsells__price">${esc(p.price)}</span>` : ''}
          </span>
          <button type="button" class="sof-upsells__add" data-handle="${esc(p.handle)}" aria-label="Add ${esc(p.title)} to cart">Add</button>
        </div>
      </article>
    `).join('');
    grid.replaceChildren(wrapper);
    wrapper.querySelectorAll('.sof-upsells__add').forEach(btn => {
      btn.addEventListener('click', (e) => this.addToCart(e, btn));
    });
  }

  // Normalize a variant/option label for comparison.
  // Examples that should all bucket together: "30x72\"", "30×72\"", "30 x 72 \"", "30x72"
  static normSize(s) {
    if (!s) return '';
    return String(s)
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[×x]/g, 'x')
      .replace(/["'”″]/g, '')
      .replace(/[-_,]/g, '');
  }

  async cartTargetSize() {
    try {
      const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!r.ok) return null;
      const cart = await r.json();
      const first = (cart.items || [])[0];
      if (!first) return null;
      // Prefer options_with_values for robustness, fall back to variant_title
      const opts = first.options_with_values || [];
      const sizeOpt = opts.find(o => /size|length|ft|roll/i.test(o.name)) || opts[0];
      if (sizeOpt && sizeOpt.value) return SofCartUpsells.normSize(sizeOpt.value);
      return SofCartUpsells.normSize(first.variant_title || '');
    } catch { return null; }
  }

  async addToCart(e, btn) {
    e.preventDefault(); e.stopPropagation();
    const handle = btn.dataset.handle;
    if (!handle || btn.disabled) return;
    const orig = btn.textContent;
    btn.disabled = true; btn.textContent = '…';
    try {
      const [pRes, target] = await Promise.all([
        fetch(`/products/${handle}.js`),
        this.cartTargetSize(),
      ]);
      const p = await pRes.json();
      const variants = p.variants || [];

      // Match by size option — prefer exact normalized match on any option value,
      // then fall back to matching variant title, then first available.
      let chosen = null;
      if (target) {
        chosen = variants.find(v =>
          (v.options || []).some(o => SofCartUpsells.normSize(o) === target)
        );
        if (!chosen) {
          chosen = variants.find(v => SofCartUpsells.normSize(v.title) === target);
        }
      }
      if (!chosen) chosen = variants.find(v => v.available) || variants[0];
      if (!chosen) throw new Error('no variant');

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{ quantity: 1, id: chosen.id }],
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      });
      if (!addRes.ok) throw new Error(`add failed: ${addRes.status}`);
      const data = await addRes.json();

      const sections = data.sections || {};
      if (sections['cart-drawer']) {
        const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const newInner = parsed.querySelector('.drawer__inner');
        const currentInner = document.querySelector('#CartDrawer .drawer__inner');
        if (newInner && currentInner) currentInner.replaceWith(newInner);
      }
      if (sections['cart-icon-bubble']) {
        const parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newBubble = parsed.querySelector('#cart-icon-bubble, .cart-count-bubble')
                          || parsed.body.firstElementChild;
        const currentBubble = document.querySelector('#cart-icon-bubble')
                              || document.querySelector('.cart-count-bubble');
        if (newBubble && currentBubble) currentBubble.replaceWith(newBubble);
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'sof-upsell' });
      }
    } catch (err) {
      console.error('sof-upsell add failed', err);
      btn.textContent = orig;
      btn.disabled = false;
    }
  }
}
if (!customElements.get('sof-cart-upsells')) {
  customElements.define('sof-cart-upsells', SofCartUpsells);
}
